import { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authClient, useSession } from '../lib/auth-client';
import { Button, Card, CardBody, Input } from '@img/ui';

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0); // ⏱️ ثواني المؤقّت لإعادة الإرسال
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  // ⏱️ عداد تنازلي
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // 🚀 لمّا يتم التسجيل بنجاح، شغّل المؤقّت تلقائياً عشان ما يضغط مباشرة
  useEffect(() => {
    if (success) setCooldown(60);
  }, [success]);

  if (isPending) return null;
  if (session?.user) return <Navigate to="/dashboard" replace />;

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const validate = (): string | null => {
    if (form.name.trim().length < 2) return 'الاسم قصير جداً';
    if (!form.email.includes('@')) return 'البريد الإلكتروني غير صحيح';
    if (form.phone.length < 9) return 'رقم الجوال غير صحيح';
    if (form.password.length < 8) return 'كلمة المرور 8 أحرف على الأقل';
    if (form.password !== form.confirmPassword) return 'كلمتا المرور غير متطابقتين';
    if (!accepted) return 'يجب الموافقة على الشروط والأحكام';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }

    setBusy(true);
    setError(null);
    try {
      const { error: signUpError } = await authClient.signUp.email({
        name: form.name,
        email: form.email,
        password: form.password,
        // الحقول الإضافية يقبلها Better Auth حسب additionalFields
        phone: form.phone,
      } as never);
      if (signUpError) {
        setError(signUpError.message ?? 'فشل التسجيل');
      } else {
        // ✅ تم التسجيل — نعرض شاشة "تحقق من بريدك" ولا نسجّل دخول تلقائي
        setSuccess(true);
      }
    } catch (err) {
      setError((err as Error).message ?? 'فشل التسجيل');
    } finally {
      setBusy(false);
    }
  };

  // ✉️ شاشة "تحقق من بريدك" بعد نجاح التسجيل
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bg-light)] py-12">
        <Card className="w-full max-w-md">
          <CardBody className="p-8 text-center">
            <div className="text-6xl mb-4">📬</div>
            <h1 className="text-2xl font-black text-navy-dark mb-2">تحقّق من بريدك</h1>
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              أرسلنا رابط تفعيل إلى:
              <br />
              <b className="text-navy-dark" dir="ltr">{form.email}</b>
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-5">
              ⚠️ لو ما لقيته في الـ Inbox، تحقق من مجلد <b>Spam</b> أو <b>Junk</b>.
            </div>
            <div className="flex gap-2 flex-col">
              <Button
                variant="ghost"
                size="md"
                disabled={cooldown > 0}
                onClick={async () => {
                  if (cooldown > 0) return;
                  try {
                    await authClient.sendVerificationEmail({
                      email: form.email,
                      callbackURL: `${window.location.origin}/login?verified=1`,
                    });
                    setResendMsg('✅ تم إعادة الإرسال');
                    setCooldown(60);
                  } catch {
                    setResendMsg('⚠️ تعذّر الإرسال');
                  }
                }}
              >
                {cooldown > 0
                  ? `⏱️ إعادة الإرسال خلال ${cooldown} ثانية`
                  : '📨 إعادة إرسال الرسالة'}
              </Button>
              {resendMsg && (
                <div className={`text-xs ${resendMsg.startsWith('✅') ? 'text-emerald-700' : 'text-red-700'}`}>
                  {resendMsg}
                </div>
              )}
              <Button variant="primary" size="md" onClick={() => navigate('/login')}>
                ↩ الذهاب لتسجيل الدخول
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bg-light)] py-12">
      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block bg-navy-dark text-white px-4 py-2 rounded-lg font-black mb-3">
              {t('app.name')}
            </Link>
            <h1 className="text-2xl font-black text-navy-dark">إنشاء حساب جديد</h1>
            <p className="text-sm text-gray-500 mt-1">انضم لمنصة شركة صورة العقارية</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="الاسم الكامل *"  value={form.name}  onChange={set('name')}  required />
            <Input label="البريد الإلكتروني *" type="email" value={form.email} onChange={set('email')} required />
            <Input label="رقم الجوال *"   type="tel" dir="ltr" value={form.phone} onChange={set('phone')} required hint="مثال: 0501234567" />
            <Input label="كلمة المرور *"   type="password" value={form.password} onChange={set('password')} required hint="8 أحرف على الأقل" />
            <Input label="تأكيد كلمة المرور *" type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required />

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 accent-navy"
              />
              <span className="text-gray-600">
                أوافق على{' '}
                <Link to="/terms" className="text-navy-dark font-bold hover:text-gold">الشروط والأحكام</Link>
                {' '}و{' '}
                <Link to="/privacy" className="text-navy-dark font-bold hover:text-gold">سياسة الخصوصية</Link>
              </span>
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}

            <Button type="submit" variant="gold" size="lg" loading={busy} className="w-full">
              إنشاء الحساب
            </Button>
          </form>

          <div className="text-center text-sm text-gray-600 mt-6">
            عندك حساب بالفعل؟{' '}
            <Link to="/login" className="text-navy-dark font-bold hover:text-gold">
              تسجيل الدخول
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
